import { FC, createContext, useContext } from 'react';

import { sum } from '@npi/buddy-utils';
import { FormikProps } from 'formik';

import { ExtendedAddress, Principal } from 'src/types/AddMerchant/merchantInfo';
import { calculateTimeAtAddressInYears } from 'src/utils/utils';

interface PrincipalAddressContext {
    onAddressIsValid?: (
        addressIndex: number,
        principals: Principal[],
        principal: Principal,
        principalIndex: number
    ) => void;
}

const PrincipalAddressContext = createContext<PrincipalAddressContext>({});

export const usePrincipalAddressContext = () => {
    return useContext(PrincipalAddressContext);
};

interface PrincipalAddressProvider {
    formik: FormikProps<any>;
}

const requiredTimeInYears = 3;

export const getTimeInYears = (currentState: ExtendedAddress[]) => {
    const timeAtAddressInYears: number[] = currentState.map(({ timeAtAddress }) => {
        const years = timeAtAddress?.years ?? 0;
        const months = timeAtAddress?.months ?? 0;

        return calculateTimeAtAddressInYears(years, months);
    });

    return sum(timeAtAddressInYears);
};

export const PrincipalAddressProvider: FC<PrincipalAddressProvider> = ({ children, formik }) => {
    // Check to run after a valid address object has been provided
    const onAddressIsValid = (
        addressIndex: number,
        principals: Principal[],
        principal: Principal,
        principalIndex: number
    ) => {
        const isLastAddress = addressIndex === principal?.addresses.length - 1;

        const timeAtAddressInYears = getTimeInYears(principal.addresses);

        if (isLastAddress && timeAtAddressInYears < requiredTimeInYears) {
            const addresses = principal?.addresses ?? [];
            addresses.push({
                country: '',
                type: 'PREVIOUS_ADDRESS',
                addressLine1: '',
                addressLine2: '',
                city: '',
                townOrCity: '',
                county: '',
                eircode: '',
                timeAtAddress: {
                    years: 0,
                    months: 0,
                },
            });
            const newPrincipal: Principal = {
                ...principal,
                addresses,
            };

            const newPrincipals = principals.map((el, index) => {
                return index === principalIndex ? newPrincipal : el;
            });

            formik.setFieldValue('principals', newPrincipals);
        } else if (!isLastAddress && timeAtAddressInYears >= requiredTimeInYears) {
            onCheckIfNextExceeds3Years(principals, principal, principalIndex);
        }
    };

    const onCheckIfNextExceeds3Years = (
        principals: Principal[],
        principal: Principal,
        principalIndex: number
    ) => {
        let timeAtAddress = 0;

        const addresses = principal.addresses.filter((principal) => {
            if (timeAtAddress >= requiredTimeInYears) {
                return false;
            }
            timeAtAddress += calculateTimeAtAddressInYears(
                principal?.timeAtAddress?.years ?? 0,
                principal?.timeAtAddress?.months ?? 0
            );

            return true;
        });

        const newPrincipal: Principal = {
            ...principal,
            addresses,
        };

        const newPrincipals = principals.map((el, index) => {
            return index === principalIndex ? newPrincipal : el;
        });

        formik.setFieldValue('principals', newPrincipals);
    };

    return (
        <PrincipalAddressContext.Provider value={{ onAddressIsValid }}>
            {children}
        </PrincipalAddressContext.Provider>
    );
};
